import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  flowThreadBranchTypes,
  flowThreadContinuationTypeMap,
  flowThreadMessageTypes,
  flowThreadStructuralTypes,
  flowThreadUiCopy,
} from './flowThreadConstants';

const mediaTypeFromMime = mime => {
  if (mime?.startsWith('image/')) return 'image';
  if (mime?.startsWith('video/')) return 'video';
  return 'document';
};

const maxInteractiveHeaderMediaSize = file => {
  if (file.type?.startsWith('image/')) return 5;
  if (file.type?.startsWith('video/')) return 16;
  return 100;
};

export const useFlowThreadContext = ({
  props,
  emit,
  rootRef,
  interactiveHeaderUploadRef,
}) => {
  const alert = useAlert;
  const uiCopy = flowThreadUiCopy;
  const quickInsertMenuTarget = ref('');
  const buttonDrafts = ref({});

  const node = computed(() => props.item.node);
  const isSelected = computed(() => node.value.id === props.selectedNodeId);

  const bubbleShellClass = computed(() => {
    if (isSelected.value) {
      return 'border-[#25d366] shadow-[0_10px_30px_rgba(37,211,102,0.14)] ring-2 ring-[#25d366]/10';
    }

    if (flowThreadMessageTypes.includes(node.value.type)) {
      return 'border-black/5 shadow-[0_8px_22px_rgba(15,23,42,0.06)]';
    }

    return 'border-black/5 shadow-[0_8px_22px_rgba(15,23,42,0.05)]';
  });

  const nodeWidthClass = computed(() => {
    if (node.value.type === 'carousel') {
      return 'max-w-[17.25rem]';
    }

    if (['image', 'video', 'audio', 'file'].includes(node.value.type)) {
      return 'max-w-[16.75rem]';
    }

    if (
      flowThreadBranchTypes.includes(node.value.type) ||
      flowThreadStructuralTypes.includes(node.value.type)
    ) {
      return 'max-w-[13.75rem]';
    }

    return 'max-w-[15.75rem]';
  });

  const branchLabel = branch =>
    branch.item?.node?.config?.title ||
    branch.item?.node?.config?.buttonText ||
    branch.item?.node?.label ||
    branch.output;

  const continuationItem = computed(() => {
    if (
      node.value.type === 'keyboard' &&
      props.item.next?.node?.type === 'section'
    ) {
      return null;
    }

    return props.item.next || null;
  });

  const structuredBranches = computed(() => {
    if (
      node.value.type === 'keyboard' &&
      props.item.next?.node?.type === 'section'
    ) {
      return props.item.next.branches || [];
    }

    return props.item.branches || [];
  });

  const listKeyboardBranch = computed(() =>
    node.value.type === 'interactive'
      ? structuredBranches.value.find(
          branch => branch.item?.node?.type === 'keyboard'
        )
      : null
  );

  const isInteractiveListNode = computed(() =>
    Boolean(listKeyboardBranch.value)
  );

  const listSections = computed(() => {
    if (node.value.type === 'keyboard') {
      return structuredBranches.value
        .filter(branch => branch.item?.node?.type === 'section')
        .map(branch => ({
          section: branch.item?.node || null,
          rows: branch.item?.branches || [],
        }));
    }

    if (!isInteractiveListNode.value) {
      return [];
    }

    const keyboardItem = listKeyboardBranch.value?.item;
    return (keyboardItem?.branches || [])
      .filter(branch => branch.item?.node?.type === 'section')
      .map(branch => ({
        section: branch.item?.node || null,
        rows: branch.item?.branches || [],
      }));
  });

  const visibleStructuredBranches = computed(() => {
    if (!isInteractiveListNode.value) {
      return structuredBranches.value;
    }

    return structuredBranches.value.filter(
      branch => branch.item?.node?.type !== 'keyboard'
    );
  });

  const interactiveButtons = computed(() =>
    structuredBranches.value.filter(
      branch => branch.item?.node?.type === 'button'
    )
  );

  const interactiveButtonContinuationItems = computed(() =>
    interactiveButtons.value
      .filter(branch => branch.item?.next)
      .map(branch => ({
        id: branch.id,
        label: branch.item?.node?.config?.title || uiCopy.branchPlaceholder,
        item: branch.item?.next,
      }))
  );

  const canAddInteractiveButton = computed(
    () =>
      node.value.type === 'interactive' && interactiveButtons.value.length < 3
  );

  const interactiveButtonPosition = buttonNodeId =>
    interactiveButtons.value.findIndex(
      branch => branch.item?.node?.id === buttonNodeId
    );

  const listRows = computed(() =>
    listSections.value.flatMap(section => section.rows || [])
  );

  const listRowCount = computed(() => listRows.value.length);

  const canAddRowToSection = sectionRows =>
    isSelected.value && sectionRows.length < 10 && listRowCount.value < 10;

  const canAddAnotherSection = computed(
    () => isSelected.value && listRowCount.value < 10
  );

  const updateNode = patch => {
    emit('updateNode', {
      nodeId: node.value.id,
      patch,
    });
  };

  const updateInteractiveHeader = patch => {
    updateNode({
      header: {
        ...(node.value.config?.header || {}),
        ...patch,
      },
    });
  };

  const setInteractiveHeaderMode = mode => {
    if (mode === 'none') {
      updateInteractiveHeader({
        enabled: false,
        type: 'text',
        text: '',
        mediaUrl: '',
        mediaLink: '',
        blobId: '',
        blobKey: '',
        fileName: '',
        fileSize: 0,
        contentType: '',
      });
      return;
    }

    updateInteractiveHeader({
      enabled: true,
      type: mode,
    });
  };

  const triggerInteractiveHeaderUpload = () => {
    interactiveHeaderUploadRef.value?.click();
  };

  const handleInteractiveHeaderUpload = async event => {
    const file = event.target.files?.[0];
    if (!file || node.value.type !== 'interactive') return;

    const maxSize = maxInteractiveHeaderMediaSize(file);
    if (file.size > maxSize * 1024 * 1024) {
      alert(uiCopy.mediaSizeError(maxSize));
      event.target.value = '';
      return;
    }

    try {
      const uploadedFile = await uploadFile(file);
      updateInteractiveHeader({
        enabled: true,
        type: 'media',
        mediaType: mediaTypeFromMime(file.type),
        mediaSource: 'uploaded',
        mediaUrl: uploadedFile.fileUrl,
        mediaLink: '',
        blobId: uploadedFile.blobId,
        blobKey: uploadedFile.blobKey,
        contentType: file.type,
        fileSize: file.size,
        fileName: file.name,
      });
    } catch (error) {
      alert(error.message || 'فشل رفع الملف');
    } finally {
      event.target.value = '';
    }
  };

  const updateBranchNode = (branch, patch) => {
    emit('updateNode', {
      nodeId: branch.item?.node?.id,
      patch,
    });
  };

  const updateListKeyboardNode = patch => {
    const keyboardId = listKeyboardBranch.value?.item?.node?.id;
    if (!keyboardId) return;

    emit('updateNode', {
      nodeId: keyboardId,
      patch,
    });
  };

  const updateListSectionNode = (sectionNodeId, patch) => {
    if (!sectionNodeId) return;

    emit('updateNode', {
      nodeId: sectionNodeId,
      patch,
    });
  };

  const branchContinuationLabel = branch =>
    branch.item?.next?.node?.config?.title ||
    branch.item?.next?.node?.config?.body ||
    branch.item?.next?.node?.summary ||
    branch.item?.next?.node?.label ||
    '-';

  const branchContinuationTypeLabel = branch =>
    flowThreadContinuationTypeMap[branch.item?.next?.node?.type] ||
    branch.item?.next?.node?.label ||
    branch.item?.next?.node?.type ||
    '';

  const branchContinuationSummary = branch => {
    const continuationLabel = branchContinuationLabel(branch);
    const continuationType = branchContinuationTypeLabel(branch);

    if (!continuationLabel || continuationLabel === '-') {
      return continuationType || '';
    }

    return continuationType && continuationType !== continuationLabel
      ? `${continuationType}: ${continuationLabel}`
      : continuationLabel;
  };

  const branchInsertItems = computed(() =>
    props.quickInsertItems.filter(
      item => item.id !== 'start' && item.id !== 'noMatch'
    )
  );

  const hasOpenInteractiveButtonMenu = computed(() =>
    interactiveButtons.value.some(
      branch =>
        quickInsertMenuTarget.value === `button_${branch.item?.node?.id}`
    )
  );

  const hasOpenQuickInsertMenu = computed(() =>
    Boolean(quickInsertMenuTarget.value)
  );

  const closeQuickInsertMenu = () => {
    quickInsertMenuTarget.value = '';
  };

  const toggleQuickInsertMenu = targetId => {
    quickInsertMenuTarget.value =
      quickInsertMenuTarget.value === targetId ? '' : targetId;
    emit('select', node.value.id);
  };

  const isQuickInsertEventInsideActiveMenu = event => {
    const activeTarget = quickInsertMenuTarget.value;
    if (!activeTarget) return false;

    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return false;

    const triggerTarget = eventTarget.closest('[data-quick-insert-trigger]')
      ?.dataset?.quickInsertTrigger;
    const menuTarget = eventTarget.closest('[data-quick-insert-menu]')?.dataset
      ?.quickInsertMenu;

    return triggerTarget === activeTarget || menuTarget === activeTarget;
  };

  const insertBranchNode = (blockType, anchorNodeId) => {
    closeQuickInsertMenu();
    emit('quickInsert', {
      blockType,
      anchorNodeId,
    });
  };

  const handleDocumentPointerDown = event => {
    if (!quickInsertMenuTarget.value) return;

    if (isQuickInsertEventInsideActiveMenu(event)) {
      return;
    }

    if (!rootRef.value || !rootRef.value.contains(event.target)) {
      closeQuickInsertMenu();
      return;
    }

    if (rootRef.value.contains(event.target)) {
      closeQuickInsertMenu();
    }
  };

  const handleDocumentKeydown = event => {
    if (event.key === 'Escape' && quickInsertMenuTarget.value) {
      closeQuickInsertMenu();
    }
  };

  const syncButtonDrafts = branches => {
    const nextDrafts = {};
    branches.forEach(branch => {
      const branchId = branch.item?.node?.id;
      if (!branchId) return;

      nextDrafts[branchId] =
        buttonDrafts.value[branchId] ?? branch.item?.node?.config?.title ?? '';
    });
    buttonDrafts.value = nextDrafts;
  };

  const buttonDraftValue = branch =>
    buttonDrafts.value[branch.item?.node?.id] ??
    branch.item?.node?.config?.title ??
    '';

  const updateButtonDraft = (branch, value) => {
    const branchId = branch.item?.node?.id;
    if (!branchId) return;

    buttonDrafts.value = {
      ...buttonDrafts.value,
      [branchId]: value,
    };
  };

  const persistButtonDraft = branch => {
    const branchId = branch.item?.node?.id;
    if (!branchId) return;

    updateBranchNode(branch, {
      title: buttonDrafts.value[branchId] || '',
    });
  };

  watch(
    () => props.selectedNodeId,
    value => {
      if (!value) {
        closeQuickInsertMenu();
      }
    }
  );

  watch(
    interactiveButtons,
    value => {
      syncButtonDrafts(value);
    },
    { immediate: true, deep: true }
  );

  onMounted(() => {
    document.addEventListener('pointerdown', handleDocumentPointerDown);
    document.addEventListener('keydown', handleDocumentKeydown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', handleDocumentPointerDown);
    document.removeEventListener('keydown', handleDocumentKeydown);
  });

  return {
    uiCopy,
    node,
    isSelected,
    bubbleShellClass,
    nodeWidthClass,
    branchLabel,
    continuationItem,
    structuredBranches,
    listKeyboardBranch,
    isInteractiveListNode,
    listSections,
    visibleStructuredBranches,
    interactiveButtons,
    interactiveButtonContinuationItems,
    canAddInteractiveButton,
    interactiveButtonPosition,
    listRows,
    canAddRowToSection,
    canAddAnotherSection,
    branchInsertItems,
    quickInsertMenuTarget,
    hasOpenInteractiveButtonMenu,
    hasOpenQuickInsertMenu,
    updateNode,
    updateInteractiveHeader,
    setInteractiveHeaderMode,
    triggerInteractiveHeaderUpload,
    handleInteractiveHeaderUpload,
    updateListKeyboardNode,
    updateListSectionNode,
    updateBranchNode,
    toggleQuickInsertMenu,
    insertBranchNode,
    buttonDraftValue,
    updateButtonDraft,
    persistButtonDraft,
    branchContinuationSummary,
  };
};
